/**
 * @fileoverview Shared Event Form Fields component.
 * @summary Common form fields used by both CreateEventForm and EventEditForm.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/organizer/components/EventFormFields
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
// import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import FloatingLabelInput from '../../../components/FloatingLabelInput'
import { utcToLocalDateString } from '../../../utils/date.js'

interface Venue {
  id: string
  name: string
  city?: string
}

interface EventFormFieldsProps {
  formData: {
    name: string
    description: string
    venueId: string
    date: string
    time: string
  }
  venues: Venue[]
  onFormDataChange: (data: {
    name?: string
    description?: string
    venueId?: string
    date?: string
    time?: string
  }) => void
  disabled?: boolean
  eventId?: string // Optional: required for quota validation on date change
  onDateQuotaError?: (error: { message: string; currentCount?: number; limit?: number }) => void // Callback when quota is exceeded
  originalDate?: string // Original event date (to detect if date changed)
  planType?: 'free' | 'one_shot' | 'subscription' // Event plan type (for ONE_SHOT date limits)
  oneShotExpiresAt?: string | Date // ONE_SHOT expiration date (for max date limit)
}

/**
 * @summary Shared event form fields component.
 * @param {EventFormFieldsProps} props - Component props.
 * @returns {React.ReactElement} Event form fields.
 */
export function EventFormFields({
  formData,
  venues,
  onFormDataChange,
  disabled = false,
  // eventId,
  // onDateQuotaError,
  // originalDate,
  planType,
  oneShotExpiresAt,
}: EventFormFieldsProps) {
  const { t } = useTranslation()
  // const [isCheckingQuota, setIsCheckingQuota] = useState(false)

  // Calculate min date (today) - applies to all events
  const today = new Date()
  const minDate = utcToLocalDateString(today) // Format YYYY-MM-DD for date input

  // Calculate max date (only for ONE_SHOT events)
  let maxDate: string | undefined
  if (planType === 'one_shot' && oneShotExpiresAt) {
    maxDate = utcToLocalDateString(oneShotExpiresAt)
  }

  /**
   * @summary Validate date quota on blur (for both creation and update)
   */
  // const handleDateBlur = async () => {
  //   // Only validate if date and time are both filled
  //   // console.log('formData', formData)
  //   if (!formData.date || disabled || isCheckingQuota) {
  //     return
  //   }

  //   // For update: Check if date actually changed
  //   if (eventId && originalDate) {
  //     const originalDateTime = new Date(originalDate)
  //     const newDateTime = new Date(`${formData.date}T00:00:00`)
      
  //     // If date hasn't changed, no need to check
  //     if (originalDateTime.getTime() === newDateTime.getTime()) {
  //       return
  //     }
  //   }

  //   setIsCheckingQuota(true)

  //   try {
  //     const { checkEventQuotaForDate, checkEventQuotaForCreation } = await import('../../../services/api.js')
  //     const dateTime = new Date(`${formData.date}T00:00:00`)
  //     const isoDateString = dateTime.toISOString()

  //     let response
  //     if (eventId) {
  //       // Update: use event-specific endpoint
  //       response = await checkEventQuotaForDate(eventId, isoDateString)
  //     } else {
  //       // Creation: use creation-specific endpoint
  //       response = await checkEventQuotaForCreation(isoDateString)
  //     }

  //     if (!response.success || !response.data?.allowed) {
  //       // Quota exceeded - call callback to show modal
  //       onDateQuotaError?.({
  //         message: response.data?.reason || response.message || t('events.quotaExceeded') || 'Quota mensuel atteint pour cette date',
  //         currentCount: response.data?.currentCount,
  //         limit: response.data?.limit,
  //       })
  //       // Revert to original date (only for update)
  //       if (eventId && originalDate) {
  //         const original = new Date(originalDate)
  //         const dateStr = original.toISOString().split('T')[0]
  //         const timeStr = original.toTimeString().slice(0, 5)
  //         onFormDataChange({ date: dateStr, time: timeStr })
  //       }
  //     }
  //   } catch (error: any) {
  //     console.error('Error checking quota:', error)
  //     // Don't block user on error, just log it
  //   } finally {
  //     setIsCheckingQuota(false)
  //   }
  // }

  return (
    <>
      <FloatingLabelInput
        id="name"
        name="name"
        type="text"
        value={formData.name}
        onChange={(e) => onFormDataChange({ name: e.target.value })}
        label={t('events.name') || 'Event Name'}
        required
        disabled={disabled}
      />

      <FloatingLabelInput
        id="description"
        name="description"
        type="text"
        value={formData.description}
        onChange={(e) => onFormDataChange({ description: e.target.value })}
        label={t('events.description') || 'Description'}
        optional
        disabled={disabled}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="relative">
          <FloatingLabelInput
            id="date"
            name="date"
            type="date"
            value={formData.date}
            onChange={(e) => onFormDataChange({ date: e.target.value })}
            // onBlur={handleDateBlur}
            label={t('events.date') || 'Date'}
            required
            disabled={disabled}
            min={minDate}
            max={maxDate}
          />
          {/* {isCheckingQuota && (
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
            </div>
          )} */}
        </div>
        <FloatingLabelInput
          id="time"
          name="time"
          type="time"
          value={formData.time}
          onChange={(e) => onFormDataChange({ time: e.target.value })}
          // onBlur={handleDateBlur}
          label={t('events.time') || 'Time'}
          required
          disabled={disabled }
        />
      </div>

      <div>
        <label htmlFor="venueId" className="input-label pr-2">
          {t('events.venue') || 'Venue'} :
        </label>
        <select
          id="venueId"
          name="venueId"
          value={formData.venueId}
          onChange={(e) => onFormDataChange({ venueId: e.target.value })}
          className="input-field-select"
          required
          disabled={disabled || venues.length === 0}
        >
          <option value="">{t('events.selectVenue') || 'Select a venue'}</option>
          {venues.map((venue) => (
            <option key={venue.id} value={venue.id}>
              {venue.name} - {venue.city}
            </option>
          ))}
        </select>
      </div>
    </>
  )
}

